<template>
  <div :id="viewerDivId"
       class="lbs-viewer-container"
       :class="{'tray-collapse': !leftSideVisible,
                'fill-browser-window': fillWindow }">
    <section v-if="fillWindow"
             class="lbs-viewer-header"
    >
      <popup-header :title="title" />
    </section>
    <section class="lbs-viewer-left-side">
      <section v-show="leftSideVisible"
               class="lbs-viewer-left-side-content"
      >
        <component v-for="(workflow, index) in workflows"
                   :key="workflow.key"
                   :is="workflow.component"
                   :config="config"
                   :is-workflow-selected="tabIndex === index"
                   :class="{'lbs-viewer-active': tabIndex === index}"/>
      </section>
      <nav class="lbs-viewer-left-side-nav">
        <button class="lbs-viewer-button lbs-viewer-collapse-button"
                @click="toggleLeftPanel()"
                :title="toggleWorkflowTitle">
          <img src="./assets/collapse-icon.png">
        </button>
        <div class="lbs-viewer-tab-container">
          <button v-for="(workflow, index) in workflows"
                  :key="workflow.key"
                  class="lbs-viewer-button lbs-viewer-tab-button"
                  :class="{'lbs-viewer-tab-selected': tabIndex === index}"
                  @click="selectTab(index)"
                  :title="$t(workflow.title)">
            <img :src="workflow.icon">
          </button>
        </div>
        <vertical-label :title="title"/>
      </nav>
    </section>
    <uscope-component :config="config"/>
  </div>
</template>

<style lang="scss">
// Prevent bootstrap styles from leaking outside and contaminating the rest of the viewer.
.lbs-scope-container {
  @import '~lbs-pi-web-styles/dist/css/bootstrap';
  @import '~bootstrap-vue/dist/bootstrap-vue';
}
</style>

<style lang="scss" scoped>
@import "~lbs-pi-web-styles/lib/_variables";
@import "./_variables";

// Viewer should fill it's parent
.lbs-viewer-container {
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
}

// Create 2-column layout with 440px area for workflows and expandable area for uScope
.lbs-viewer-container {
  display: -ms-grid; // IE11 support
  display: grid;

  -ms-grid-columns: $left-side-max-width 1fr;
  grid-template-columns: $left-side-max-width 1fr;

  -ms-grid-rows: auto minmax($viewer-min-height-IE11, 1fr);
  grid-template-rows: auto minmax($viewer-min-height, 1fr);

  grid-template-areas: "popup-header popup-header"
                        "left-side right-side";

  &.tray-collapse {
    -ms-grid-columns: $left-side-min-width 1fr;
    grid-template-columns: $left-side-min-width 1fr;
  }

  &.fill-browser-window {
    position: fixed;
    position: -ms-page; // IE11 support
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    z-index: 999; // TODO: get a handle on z-indexes in this app.
    background: $leica-white; // ensures content behind WV doesn't bleed through
  }
}

.lbs-viewer-header {
  -ms-grid-row: 1;
  -ms-grid-column: 1;
  -ms-grid-column-span: 2;
  grid-area: popup-header;
}

// Workflow Area Layout: Resizable left area, fixed-width "handle" on the right
.lbs-viewer-left-side {
  -ms-grid-row: 2;
  -ms-grid-column: 1;
  grid-area: left-side;

  display: flex;

  .lbs-viewer-left-side-content {
    flex-grow: 1;
  }
  > nav,
  .lbs-viewer-tab-container {
    display: flex;
    flex-direction: column;
    max-width: 100px;
    z-index: 1;
  }

  // Space the buttons out
  > nav {
    padding-top: 4px;
  }
  .lbs-viewer-tab-button {
    margin-top: 7px;
    height: 100px;
  }
}

// Tab Styling and Selection
$button-color: $leica-grey-light;
$button-color-selected: $leica-white;
.lbs-viewer-button {
  border: none;
  border-radius: 0 5px 5px 0;
  background: $button-color;
  padding: 5px;
  margin-right: 5px;

  &:focus {
    outline: none;
  }

  &:hover {
    background: lighten($button-color, 5%);
  }

  &.lbs-viewer-tab-selected {
    background: $button-color-selected;
    &:hover {
      background: darken($button-color-selected, 5%);
    }
  }
}

// colors
.lbs-viewer-left-side > nav {
  background: $leica-grey-medium;
}

// Animation - expanding workflow area
.lbs-viewer-left-side {
  transition: min-width 500ms;

  .lbs-viewer-left-side-content {
    width: $left-side-max-width;;
    transition: width 500ms;
  }
  // Hide the overflow to keep the slide tray from looking all weird when collapsed
  overflow: hidden;
}

.lbs-viewer-left-side-content {
  // Positioning anchor for child elements
  position: relative;

  // Make immediate children slide in
  > * {
    position: absolute;
    width: 100%;
    right: 100%;
    transition: right 0.3s ease-in-out;

    &.lbs-viewer-active {
      right: 0;
    }
  }
}

// Rotating "collapse" button
.lbs-viewer-collapse-button img {
  transition: transform 500ms;
  transition-delay: 500ms;
}

.tray-collapse .lbs-viewer-collapse-button img {
  transform: rotate(180deg);
}

// Hide tabs when collapsed
.lbs-viewer-tab-container {
  position: relative;
}

.lbs-viewer-tab-button {
  position: relative;
  left: 0;
  transition: left 500ms;
  transition-delay: 500ms;
}

.tray-collapse .lbs-viewer-tab-button {
  left: -100%;
}
</style>

<script>
  import PopupHeader from './PopupHeader';
  import VerticalLabel from './VerticalLabelComponent';
  import UscopeComponent from './UscopeComponent';

  export default {
    name: 'LbsPiWebViewer',
    i18nOptions: { namespaces: 'webviewer', keyPrefix: 'viewer' },
    components: {
      PopupHeader,
      VerticalLabel,
      UscopeComponent
    },
    props: {
      viewer: {
        type: Object,
        required: true
      },
      onLoad: {
        type: Function,
        required: true
      }
    },
    data () {
      return {
        vil: null,
        leftSideVisible: true,
        tabIndex: 0,
        title: 'Leica Web Viewer', // need to default since $t is not available yet. Use computed prop instead?
        fillWindow: false
      };
    },
    computed: {
      id () {
        return this.viewer.id;
      },
      config () {
        return this.viewer.config;
      },
      workflows () {
        return this.viewer.workflows;
      },
      viewerDivId () {
        return 'lbs-pi-viewer-' + this.id;
      },
      toggleWorkflowTitle () {
        return this.leftSideVisible ? this.$t('hide-workflow-title') : this.$t('show-workflow-title');
      }
    },
    methods: {
      selectTab (index) {
        this.tabIndex = index;
      },
      toggleLeftPanel () {
        this.leftSideVisible = !this.leftSideVisible;
        this.delayResizeUscope();
      },
      vilLoaded (data) {
        if (data) {
          if (data.hasOwnProperty('Alert')) {
            setTimeout(() => {
              alert(this.$t('error-info') + data.Alert);
            }, 3);
          }
        } else {
          alert(this.$t('error-info'));
        }

        this.onLoad && this.onLoad(this.vil);
      },
      delayResizeUscope () {
        // Wait to apply the resize because resize is getting called before height and width applied to viewer
        setTimeout(() => {
          this.vil.uScopeResize();
        }, 100);
      }
    },
    beforeMount () {
      this.$events.on('dst:summary-title-with-slide-count:change', title => {
        this.title = title;
      });
      this.$events.on('us:resize-viewer:popup-header', () => {
        this.fillWindow = !this.fillWindow;

        this.delayResizeUscope();
      });
    },
    mounted () {
      this.vil = $(this.$el).vil(
        this.config.legacyConfig,
        this.$events
      );
      this.vil.getStartupPromise()
        .then(this.vilLoaded);
    }
  };
</script>



// WEBPACK FOOTER //
// src/viewer/components/ViewerComponent.vue